// Settings → Digital Waste Tracking (Defra Receipt of Waste API).
//
// Admin-only. Per-company receiver / carrier defaults and the Defra API code.
// The API code is write-only: the server returns only a masked value, and
// leaving the field blank keeps the stored code. Vendor client id/secret are
// server environment variables and are never shown here.

import { useEffect, useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  fetchDwtSettings,
  saveDwtSettings,
  type DwtReason,
  type DwtSettingsDto,
} from '../../../api/dwtSettings';
import { useToast } from '../../../hooks/useToast';
import styles from './DwtSettingsPage.module.css';

const TRANSPORT = ['Road', 'Rail', 'Air', 'Sea', 'Inland Waterway', 'Piped', 'Other'];
const REASONS: { value: DwtReason; label: string }[] = [
  { value: 'ON_SITE', label: 'Movement within the same premises' },
  { value: 'HOUSEHOLD', label: 'Householder transporting own waste' },
  { value: 'ONE_OFF', label: 'One-off movement' },
  { value: 'MARINE', label: 'Marine transport' },
];

interface FormState {
  enabled: boolean;
  environment: 'test' | 'production';
  apiCode: string;
  receiverSiteName: string;
  receiverAuth: string;
  receiverAddress: string;
  receiverPostcode: string;
  carrierName: string;
  carrierReg: string;
  carrierReason: DwtReason | '';
  transport: string;
}

function toForm(d: DwtSettingsDto): FormState {
  return {
    enabled: d.enabled,
    environment: d.environment,
    apiCode: '',
    receiverSiteName: d.receiver_site_name ?? '',
    receiverAuth: d.receiver_authorisation_number ?? '',
    receiverAddress: d.receiver_address_full ?? '',
    receiverPostcode: d.receiver_postcode ?? '',
    carrierName: d.carrier_organisation_name ?? '',
    carrierReg: d.carrier_registration_number ?? '',
    carrierReason: d.carrier_reason_no_registration ?? '',
    transport: d.carrier_means_of_transport,
  };
}

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function DwtSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dto, setDto] = useState<DwtSettingsDto | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDwtSettings()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setForm(toForm(d));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load Digital Waste Tracking settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function handleSave() {
    if (!form) return;
    if (form.carrierReg.trim() !== '' && form.carrierReason !== '') {
      toast.error('Provide either a carrier registration number or a reason, not both');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveDwtSettings({
        enabled: form.enabled,
        environment: form.environment,
        ...(form.apiCode.trim() !== '' ? { api_code: form.apiCode.trim() } : {}),
        receiver_site_name: orNull(form.receiverSiteName),
        receiver_authorisation_number: orNull(form.receiverAuth),
        receiver_address_full: orNull(form.receiverAddress),
        receiver_postcode: orNull(form.receiverPostcode),
        carrier_organisation_name: orNull(form.carrierName),
        carrier_registration_number: orNull(form.carrierReg),
        carrier_reason_no_registration: form.carrierReason === '' ? null : form.carrierReason,
        carrier_means_of_transport: form.transport,
      });
      setDto(saved);
      setForm(toForm(saved));
      toast.success('Digital Waste Tracking settings saved');
    } catch {
      toast.error('Failed to save settings. Check the API code is a valid UUID.');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form || !dto) {
    return (
      <div className={styles.page}>
        <PageHeader title="Digital Waste Tracking" />
        <Card>
          <div className={styles.spinnerWrap}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Digital Waste Tracking"
        subtitle="Receiver and carrier defaults for reporting waste receipts to Defra."
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings')}>
            Back
          </Button>
        }
      />

      <Card>
        <div className={styles.form}>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            Enable Digital Waste Tracking for this company
          </label>

          <div className={styles.field}>
            <label htmlFor="dwt-env">Environment</label>
            <select
              id="dwt-env"
              value={form.environment}
              onChange={(e) => set('environment', e.target.value as 'test' | 'production')}
            >
              <option value="test">Test</option>
              <option value="production">Production</option>
            </select>
            <small>
              Server credentials: test {dto.vendor_credentials.test ? 'configured' : 'missing'},
              production {dto.vendor_credentials.production ? 'configured' : 'missing'}.
              Production calls are {dto.production_calls_allowed ? 'allowed' : 'blocked'} on this
              server.
            </small>
          </div>

          <div className={styles.field}>
            <label htmlFor="dwt-apicode">API code</label>
            <input
              id="dwt-apicode"
              type="text"
              autoComplete="off"
              placeholder={
                dto.api_code_set ? `Stored (${dto.api_code_masked ?? ''}). Leave blank to keep.` : 'UUID'
              }
              value={form.apiCode}
              onChange={(e) => set('apiCode', e.target.value)}
            />
          </div>

          <h3 className={styles.heading}>Receiver site</h3>
          <div className={styles.field}>
            <label htmlFor="dwt-site">Site name</label>
            <input id="dwt-site" value={form.receiverSiteName} maxLength={255} onChange={(e) => set('receiverSiteName', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-auth">Permit / authorisation number</label>
            <input id="dwt-auth" value={form.receiverAuth} maxLength={100} onChange={(e) => set('receiverAuth', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-addr">Site address</label>
            <input id="dwt-addr" value={form.receiverAddress} maxLength={500} onChange={(e) => set('receiverAddress', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-pc">Postcode</label>
            <input id="dwt-pc" value={form.receiverPostcode} maxLength={12} onChange={(e) => set('receiverPostcode', e.target.value)} />
          </div>

          <h3 className={styles.heading}>Carrier defaults</h3>
          <div className={styles.field}>
            <label htmlFor="dwt-cname">Organisation name</label>
            <input id="dwt-cname" value={form.carrierName} maxLength={255} onChange={(e) => set('carrierName', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-creg">Registration number</label>
            <input id="dwt-creg" value={form.carrierReg} maxLength={100} onChange={(e) => set('carrierReg', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-creason">Reason for no registration</label>
            <select id="dwt-creason" value={form.carrierReason} onChange={(e) => set('carrierReason', e.target.value as DwtReason | '')}>
              <option value="">None</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-transport">Default means of transport</label>
            <select id="dwt-transport" value={form.transport} onChange={(e) => set('transport', e.target.value)}>
              {TRANSPORT.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.actions}>
            <Button icon={Save} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default DwtSettingsPage;
